import { Grid } from "@mui/material";
import _ from "lodash";
import { observer } from "mobx-react";
import React, { useMemo, useState } from "react";
import {
    ApplicationFilterItemsStore,
    FilterKey,
    IApplicationFilterItem,
} from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import {
    ApplicationFilters,
    ApplicationFiltersStore,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { ApplicationFiltersInstanceStore } from "stores/ApplicationFilters/ApplicationFiltersInstanceStore";
import { useStore } from "utils/useStore";
import { FilterChip } from "./BaseChipComponents/FilterChip";
import { useResizeObserver } from "utils/useResizeObserver";
import { getGroupedExtendedMetadataValue } from "utils/extendedMetadata";
import { getAppliedFilterStateByKey } from "./BaseChipComponents/appliedFilterChipState";

interface AppliedFiltersChipsProps {
    /**
     * A list of filter keys that will not be rendered as chips
     */
    ignoreFilterKeys?: FilterKey[];
    /**
     * A list of filter keys that, while selectable, are not applied
     * to queries in the current context.
     */
    nonApplicableFilterKeys?: FilterKey[];

    disabled?: boolean;
    store?: ApplicationFiltersInstanceStore;
    filters?: ApplicationFilters;
    filterItems?: IApplicationFilterItem[];
    allowedFilterKeys?: FilterKey[];
    renderDateRangeFirst?: boolean;
    hideDateRangeChip?: boolean;
    appliedFilters?: ApplicationFilters | null;
    extraChips?: React.ReactNode[];
}

function isFilterItemApplicable(
    filterItem: IApplicationFilterItem,
    nonApplicableFilterKeys?: FilterKey[],
) {
    if (!nonApplicableFilterKeys || nonApplicableFilterKeys.length === 0)
        return true;

    const isExtendedMetadataItem =
        filterItem.filterKey.startsWith("extendedMetadata");

    for (const nonApplicableFilterKey of nonApplicableFilterKeys) {
        // Any keys starting with extendedMetadata should be ignored
        // when extendedMetadata is a non applicable filter key
        if (
            isExtendedMetadataItem &&
            (nonApplicableFilterKey === "extendedMetadata" ||
                nonApplicableFilterKey === "extendedMetadataGrouped")
        ) {
            return false;
        } else if (filterItem.filterKey === nonApplicableFilterKey) {
            return false;
        }
    }

    return true;
}

const AppliedFiltersChips = observer(function (
    props: AppliedFiltersChipsProps,
) {
    const globalApplicationFiltersStore = useStore(ApplicationFiltersStore);
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
    const {
        disabled,
        ignoreFilterKeys,
        nonApplicableFilterKeys,
        renderDateRangeFirst,
        store,
        filters,
        allowedFilterKeys,
        hideDateRangeChip,
        appliedFilters,
        extraChips,
    } = props;

    const applicationFiltersStore = useMemo(() => {
        if (filters) {
            const instanceStore = new ApplicationFiltersInstanceStore(
                globalApplicationFiltersStore.rootStore,
                "Applied Filters Chips Store",
            );
            instanceStore.applyExternalFilters(_.cloneDeep(filters));

            // applyExternalFilters updates words/phrases through a nested reaction.
            // Set the concrete value directly so chip visibility/labels use the
            // committed search string in this same render pass.
            instanceStore.setWordsAndPhrasesSearchString(
                filters.wordsAndPhrasesSearchString,
            );

            return instanceStore;
        }

        return store ?? globalApplicationFiltersStore;
    }, [filters, globalApplicationFiltersStore, store]);
    const explicitChipStore = filters ? applicationFiltersStore : store;

    const appliedFilterItems = props.filterItems
        ? props.filterItems
        : applicationFilterItemsStore.getAppliedFilterItems(
              applicationFiltersStore,
          );
    const currentFilters = applicationFiltersStore.toFilterObject();
    const filterAppliedStateByKey = getAppliedFilterStateByKey({
        appliedFilters,
        currentFilters,
        filterItems: appliedFilterItems,
    });

    const [isExpanded, setIsExpanded] = useState(false);
    const [maxHeight, setMaxHeight] = useState<string>("61px");

    const {
        elementHeight,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        elementWidth,
        theRef,
    } = useResizeObserver<HTMLElement>({
        name: "ApplicationFilterChips",
    });

    const shownFilterItems = useMemo(
        () =>
            appliedFilterItems
                // remove ignored filter items
                .filter(
                    (item) =>
                        !ignoreFilterKeys ||
                        ignoreFilterKeys.length === 0 ||
                        !ignoreFilterKeys.includes(item.filterKey),
                )
                .filter(
                    (item) =>
                        !allowedFilterKeys ||
                        allowedFilterKeys.length === 0 ||
                        allowedFilterKeys.includes(item.filterKey),
                )
                .filter(
                    (item) =>
                        !hideDateRangeChip || item.filterKey !== "dateRange",
                )
                // sorts non applicable keys/chips to the end
                .sort((_, item) =>
                    nonApplicableFilterKeys?.includes(item.filterKey) ? -1 : 1,
                )
                .sort((left, right) => {
                    if (!renderDateRangeFirst) return 0;
                    if (
                        left.filterKey === "dateRange" &&
                        right.filterKey !== "dateRange"
                    ) {
                        return -1;
                    }
                    if (
                        right.filterKey === "dateRange" &&
                        left.filterKey !== "dateRange"
                    ) {
                        return 1;
                    }
                    return 0;
                })
                .map((item) => {
                    const groupedFilterValue = getGroupedExtendedMetadataValue(
                        currentFilters,
                        item.filterKey,
                    );
                    const filterValue = _.get(currentFilters, item.filterKey);

                    if (item.filterKey === "dateRange") {
                        if (!currentFilters.startDate || !currentFilters.endDate) {
                            return null;
                        }
                    } else if (item.filterKey === "callDuration") {
                        if (
                            (!currentFilters.minCallDuration) &&
                            (!currentFilters.maxCallDuration)
                        ) {
                            return null;
                        }
                    } else if (item.filterKey === "messageCount") {
                        if (
                            (!currentFilters.minMessageCount) &&
                            (!currentFilters.maxMessageCount)
                        ) {
                            return null;
                        }
                    } else if (groupedFilterValue) {
                        if (
                            !groupedFilterValue ||
                            groupedFilterValue.values.length === 0
                        )
                            return null;
                    } else if (
                        filterValue === undefined ||
                        filterValue === null ||
                        (typeof filterValue === "string" &&
                            filterValue.trim() === "") ||
                        (Array.isArray(filterValue) && filterValue.length === 0)
                    ) {
                        return null;
                    }

                    const isApplicable = isFilterItemApplicable(
                        item,
                        nonApplicableFilterKeys,
                    );
                    const isApplied =
                        isApplicable &&
                        (filterAppliedStateByKey[item.filterKey] ?? true);

                    return (
                        <FilterChip
                            disabled={disabled}
                            isApplicable={isApplied}
                            item={item}
                            key={item.filterKey}
                            store={explicitChipStore}
                        />
                    );
                })
                .filter((i) => i !== null), // filter out null items to avoid showing showMore after clearing as clear does not empty nulls from array
        [
            appliedFilterItems,
            disabled,
            ignoreFilterKeys,
            allowedFilterKeys,
            hideDateRangeChip,
            nonApplicableFilterKeys,
            renderDateRangeFirst,
            explicitChipStore,
            filterAppliedStateByKey,
            currentFilters,
        ],
    );

    const showShowMore = (elementHeight ?? 0) > 60 && !!shownFilterItems.length;

    return (
        <>
            <Grid
                sx={{ overflow: "hidden", transition: "max-height 0.3s ease" }}
                style={{ maxHeight }}
            >
                <Grid
                    ref={theRef}
                    container
                    direction="row"
                    alignItems="center"
                    gap={0.5}
                    flexWrap="wrap"
                    component={"span"} // needs to be a span so the ref will work for some reason
                    overflow={"hidden"}
                >
                    {shownFilterItems}
                    {extraChips}
                </Grid>
            </Grid>
            {showShowMore && (
                <div
                    style={{ cursor: "pointer", display: "inline-block" }}
                    onClick={() => {
                        if (!theRef.current) return;

                        setMaxHeight(
                            isExpanded
                                ? "61px"
                                : `${theRef.current.scrollHeight}px`,
                        );
                        setIsExpanded((prev) => !prev);
                    }}
                >
                    <span
                        style={{ textDecoration: "none" }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = "none";
                        }}
                    >
                        {isExpanded ? "Show Less" : "Show More"}
                    </span>
                </div>
            )}
        </>
    );
});

export default AppliedFiltersChips;
